import React, {useEffect, useState} from "react";
import {AccessReviewList, AccessReviewRestControllerApiFactory, UserAccessDto} from "arteco-api-client-ts";
import AppList, {AppListConfig} from "../../../../components/layout/list";
import dayjs from "dayjs";
import {FilterChips, Panel, tableContainerSx, tableSx} from "../../../../components/kit";
import {Stack} from "@mui/material";
import DownloadRoundedIcon from "@mui/icons-material/DownloadRounded";
import {USER_TYPE} from "../../config/labels";
import {Alert, Box, Button, Chip, Table, TableBody, TableCell, TableHead, TableRow, Typography} from "@mui/material";
import {useApiManager} from "../../../../components/utils/api";
import {COLOR_DISMISSED} from "../../../../components/layout/colors";

type AccessSystem = "EXTRANET" | "GITEA" | "REDMINE";

const SYSTEMS: { value: AccessSystem, label: string }[] = [
	{value: "EXTRANET", label: "Extranet"},
	{value: "GITEA", label: "Gitea"},
	{value: "REDMINE", label: "Redmine"},
];

/** En Gitea y Redmine solo hay dos niveles: administrador o usuario. */
const roleLabel = (system: AccessSystem, r: string) =>
	system === "EXTRANET" ? USER_TYPE[r.replace(/^ROLE_/, "")] ?? r : r === "ADMIN" ? "Administrador" : "Usuario";

/**
 * Revisión periódica de usuarios y permisos (#13720): quién tiene acceso hoy, con qué rol y qué
 * hay que mirar; y el histórico de revisiones, cada una con su foto en CSV. Desde #13849, también
 * las cuentas de Gitea y Redmine, leídas de su API.
 */
export const AccessReviewListPage = () => {
	const apiManager = useApiManager();
	const ctrl = apiManager.factory(AccessReviewRestControllerApiFactory);
	const [system, setSystem] = useState<AccessSystem>("EXTRANET");
	const [users, setUsers] = useState<UserAccessDto[]>();
	const [error, setError] = useState<string>();
	const systemLabel = SYSTEMS.find(s => s.value === system)!.label;

	useEffect(() => {
		setUsers(undefined);
		setError(undefined);
		apiManager.execute(ctrl.usersAccessReview(system)).then(setUsers)
			.catch(() => setError(`No se han podido leer las cuentas de ${systemLabel}.`));
	}, [system]);

	const descargarCsv = () => {
		apiManager.executeRaw(ctrl.usersCsvAccessReview(system, {responseType: "text"})).then((resp) => {
			const blob = new Blob([resp.data as unknown as string], {type: "text/csv;charset=utf-8"});
			const url = window.URL.createObjectURL(blob);
			const a = document.createElement("a");
			a.href = url;
			a.download = `accesos-${system.toLowerCase()}-${new Date().toISOString().substring(0, 10)}.csv`;
			document.body.appendChild(a);
			a.click();
			a.remove();
			window.URL.revokeObjectURL(url);
		});
	};

	const conAvisos = (users || []).filter(u => (u.warnings || []).length > 0).length;
	const subtitle = !users ? "Cargando…"
		: system === "EXTRANET"
			? `${users.length} usuarios con algún rol más allá de público · ${conAvisos} con avisos. Al registrar una revisión de la Extranet se guarda esta foto como adjunto.`
			: `${users.length} cuentas activas · ${conAvisos} con avisos. Las de administrador son los privilegios que revisa el responsable de seguridad. Al registrar una revisión de ${systemLabel} se guarda esta foto como adjunto.`;

	const listConfig: AppListConfig<AccessReviewList> = {
		title: "Revisiones de accesos",
		onClick: "/app/admin/compliance/access-review",
		headers: [
			{title: "Fecha"},
			{title: "Sistema"},
			{title: "Revisor"},
			{title: "Usuarios"},
			{title: "Próxima"},
		],
		decorator: (bean: AccessReviewList) => (
			<>
				<TableCell style={{color: bean.dismissed ? COLOR_DISMISSED : ""}}>{bean.reviewDate ? dayjs(bean.reviewDate).format("DD/MM/YYYY") : "—"}</TableCell>
				<TableCell>{bean.scope}</TableCell>
				<TableCell>{(bean.reviewer as any)?.surnamesAndName ?? "-"}</TableCell>
				<TableCell>{bean.usersReviewed ?? "-"}</TableCell>
				<TableCell>{bean.nextReviewDate ? dayjs(bean.nextReviewDate).format("DD/MM/YYYY") : "—"}</TableCell>
			</>
		),
		supplier: async (currentPage, size, term, showDismissed) => {
			return ctrl.pageAccessReview(currentPage, size, undefined, term, showDismissed);
		},
	};

	return (
		<>
			<AppList config={listConfig}/>

			<Panel sx={{mt: 3}} title="Cuentas con acceso hoy" subtitle={subtitle}
				   action={<Button size="small" variant="outlined" startIcon={<DownloadRoundedIcon/>} onClick={descargarCsv}
								   disabled={!users || users.length === 0}>Descargar CSV</Button>}>
				<Box sx={{mb: 2}}>
					<FilterChips options={SYSTEMS} value={system} onChange={setSystem}/>
				</Box>
				{error && <Alert severity="warning" sx={{mb: 2}}>{error}</Alert>}
				<Box sx={{...tableContainerSx, overflowX: "auto", minWidth: 0}}>
					<Table size="small" sx={{...tableSx, minWidth: 760}}>
						<TableHead>
							<TableRow>
								<TableCell>Usuario</TableCell><TableCell>Roles</TableCell><TableCell>Persona</TableCell>
								<TableCell>Último acceso</TableCell><TableCell>Avisos</TableCell>
							</TableRow>
						</TableHead>
						<TableBody>
							{(users || []).map(u => (
								<TableRow key={u.username}>
									<TableCell sx={{fontWeight: 600}}>{u.username}</TableCell>
									<TableCell>
										<Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
											{(u.roles || []).map(r => <Chip key={r} size="small" variant="outlined" label={roleLabel(system, r)}/>)}
										</Stack>
									</TableCell>
									<TableCell>{u.personName ?? "-"}{u.personLeftOn ? ` (baja ${u.personLeftOn})` : ""}</TableCell>
									<TableCell>{u.lastLogin ? String(u.lastLogin).replace("T", " ").substring(0, 16) : "-"}</TableCell>
									<TableCell>
										{(u.warnings || []).map(w => <Chip key={w} size="small" color="warning" label={w} sx={{mr: 0.5, mb: 0.5}}/>)}
									</TableCell>
								</TableRow>
							))}
						</TableBody>
					</Table>
				</Box>
			</Panel>
		</>
	);
};
